import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import regionService from '../../services/region.service';
import companyService from '../../services/company.service';
import '../../styles/AddPages.css';

const EditRegion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({ name: '', companyId: '', companyName: '' });
  const [companies, setCompanies]     = useState([]);
  const [errors, setErrors]           = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        // Load region
        const res = await regionService.getRegionById(id);
        if (res.success) {
          const r = res.data;
          setFormData({
            name:        r.name || '',
            companyId:   String(r.company?.id || r.companyId || ''),
            companyName: r.company?.name || '',
          });
        } else {
          alert('Region not found');
          navigate('/region/view');
          return;
        }

        // Load companies list for super admin dropdown
        if (isSuperAdmin) {
          const cRes = await companyService.getAllCompanies();
          if (cRes.success && cRes.data) setCompanies(cRes.data);
        }
      } catch {
        alert('Failed to load region');
        navigate('/region/view');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim())               e.name      = 'Region name is required';
    if (isSuperAdmin && !formData.companyId) e.companyId = 'Company is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = { name: formData.name.trim() };
      if (isSuperAdmin) payload.companyId = parseInt(formData.companyId);

      const res = await regionService.updateRegion(id, payload);
      if (res.success) {
        alert('Region updated successfully!');
        navigate(`/region/view/${id}`);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update region');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: '#6c757d' }}>Loading region...</div>;

  return (
    <div className="add-region-page">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/region/view">Regions</a></li>
          <li className="breadcrumb-item"><a href={`/region/view/${id}`}>{formData.name}</a></li>
          <li className="breadcrumb-item active">Edit</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Edit Region</h4>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-row">

              {/* Company — dropdown for SA, read-only for others */}
              {isSuperAdmin ? (
                <div className="form-group">
                  <label className="form-label">Select Company <span className="required">*</span></label>
                  <select name="companyId" className={`form-input ${errors.companyId ? 'error' : ''}`}
                    value={formData.companyId} onChange={handleChange}>
                    <option value="">Select Company</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {errors.companyId && <span className="error-message">{errors.companyId}</span>}
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Company</label>
                  <input type="text" className="form-input" value={formData.companyName} readOnly disabled
                    style={{ backgroundColor: '#e6fffa', color: '#234e52', fontWeight: 500 }} />
                </div>
              )}

              {/* Region Name */}
              <div className="form-group">
                <label className="form-label">Region Name <span className="required">*</span></label>
                <input type="text" name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name} onChange={handleChange}
                  placeholder="Enter region name (e.g., North Zone)" />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate(`/region/view/${id}`)} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : '✓ Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRegion;